import classNames from 'classnames';
import React, { useContext } from 'react';

import { SponsoredTag } from '@zooplus/ui-components';

import AppContext from '../../context';
import { trackSearchSuggestionClick } from '../../tracking/searchTracking';
import { TrackableSuggestionType } from '../../tracking/types';
import { Translations } from '../../types';
import Link from '../Link';
import { LinkTypeEnum } from '../Link/Link';
import {
    SUGGESTION_TYPE,
    UiSuggestionSection,
} from '../SearchBar/engines/types';

import styles from './SearchSuggestion.module.scss';

const checkSuggestions = (
    suggestions: UiSuggestionSection[],
    term: string,
    translations: Translations,
    searchFlyoutSponsoredTagEnabled: boolean,
    siteId: number,
    locale: string,
) => {
    const categorySection = suggestions.find(
        (s) => s.type === SUGGESTION_TYPE.CATEGORY,
    );
    const productSection = suggestions.find(
        (s) => s.type === SUGGESTION_TYPE.PRODUCT,
    );
    const serviceSection = suggestions.find(
        (s) => s.type === SUGGESTION_TYPE.SERVICE,
    );

    const trackClick =
        (type: TrackableSuggestionType, position: number) => () =>
            trackSearchSuggestionClick({ type, position, siteId, locale });

    let layout: React.JSX.Element | null = null;

    if (categorySection || productSection || serviceSection) {
        layout = (
            <>
                {categorySection && (
                    <>
                        <span className={styles.searchHeader}>
                            {`${translations.get(
                                'shop.header.search.autocomplete.title.suggestion',
                            )}`}{' '}
                            <span className={styles.searchTerm}>{term}</span>
                        </span>
                        {categorySection.items.map((category, index) => (
                            <div
                                className={styles.suggestionItem}
                                key={`item-${category.title}`}>
                                <Link
                                    bold={false}
                                    dataZta="search-flyout-suggestion-category-link"
                                    linkType={LinkTypeEnum.NEUTRAL}
                                    href={category.url}
                                    onClick={trackClick(
                                        SUGGESTION_TYPE.CATEGORY,
                                        index,
                                    )}
                                    className={classNames(
                                        styles.suggestionLink,
                                        styles.suggestionLinkCategory,
                                    )}>
                                    <span
                                        className={styles.truncatedText}
                                        dangerouslySetInnerHTML={{
                                            __html: `${category.title}`,
                                        }}
                                    />
                                    {category.count && (
                                        <span>{`(${category.count})`}</span>
                                    )}
                                </Link>
                            </div>
                        ))}
                    </>
                )}

                {productSection && (
                    <>
                        <span
                            className={classNames(
                                styles.searchHeader,
                                styles.searchHeaderMargin,
                            )}>
                            {translations.get(
                                'shop.header.search.autocomplete.title.products',
                            )}
                        </span>
                        {productSection.items.map((product, index) => (
                            <div
                                className={styles.suggestionItem}
                                key={`item-${product.title}`}>
                                <Link
                                    bold={false}
                                    linkType={LinkTypeEnum.NEUTRAL}
                                    dataZta="search-flyout-suggestion-product-link"
                                    href={product.url}
                                    onClick={trackClick(
                                        SUGGESTION_TYPE.PRODUCT,
                                        index,
                                    )}
                                    className={styles.suggestionLink}>
                                    <span
                                        className={styles.productTitleWrapper}>
                                        <img
                                            className={styles.searchImage}
                                            src={product.picture}
                                            alt=""
                                        />
                                        <span
                                            className={styles.truncatedText}
                                            dangerouslySetInnerHTML={{
                                                __html: product.title,
                                            }}
                                        />
                                        {searchFlyoutSponsoredTagEnabled &&
                                            product.isSponsored && (
                                                <SponsoredTag
                                                    className={
                                                        styles.sponsoredTag
                                                    }>
                                                    {translations.get(
                                                        'general.sponsored.products',
                                                    )}
                                                </SponsoredTag>
                                            )}
                                    </span>
                                </Link>
                            </div>
                        ))}
                    </>
                )}

                {serviceSection && (
                    <>
                        <span
                            className={classNames(
                                styles.searchHeader,
                                styles.searchHeaderMargin,
                            )}>
                            {translations.get(
                                'shop.header.search.autocomplete.title.services',
                            )}
                        </span>
                        {serviceSection.items.map((service, index) => (
                            <div
                                className={styles.suggestionItem}
                                key={`item-${service.title}`}>
                                <Link
                                    bold={false}
                                    linkType={LinkTypeEnum.NEUTRAL}
                                    href={service.url}
                                    dataZta="search-flyout-suggestion-service-link"
                                    onClick={trackClick(
                                        SUGGESTION_TYPE.SERVICE,
                                        index,
                                    )}
                                    className={styles.suggestionLink}>
                                    <span
                                        className={styles.truncatedText}
                                        dangerouslySetInnerHTML={{
                                            __html: service.title,
                                        }}
                                    />
                                </Link>
                            </div>
                        ))}
                    </>
                )}
            </>
        );
    }

    return layout;
};

export interface SearchSuggestionProps {
    suggestions: UiSuggestionSection[];
    term: string;
    id: string;
    device: 'mobile' | 'desktop';
}

const SearchSuggestion = ({
    suggestions,
    term,
    device,
}: SearchSuggestionProps): React.JSX.Element => {
    const { translations, searchFlyoutSponsoredTagEnabled, siteId, locale } =
        useContext(AppContext);
    return (
        <div
            className={styles.suggestionList}
            data-zta={`search_suggestions_${device}`}>
            {checkSuggestions(
                suggestions,
                term,
                translations,
                searchFlyoutSponsoredTagEnabled,
                siteId,
                locale,
            )}
        </div>
    );
};

export default SearchSuggestion;
